<template>
  <v-card>
    <v-form ref="form" v-model="valid" :lazy-validation="true">
      <v-card-title>Ersättningskort</v-card-title>
      <v-card-actions>
        <v-icon class="close" @click="close()">$sl-close</v-icon>
      </v-card-actions>

      <v-card-text class="mt-n2">
        <template v-if="mode === 'create'">
          <h6 class="mt-6 mb-4">Vad är anledningen till att du beställer nytt kort?</h6>
          <v-select
            v-model="input.reason"
            :items="reasons"
            item-text="text"
            outlined
            item-value="id"
            label="Välj orsak"
            data-cy="selectedReason"
            class="mb-6"
            :hint="displayOrderCost()"
            persistent-hint
            return-object
          ></v-select>

          <!-- Describe other reason here -->
          <template v-if="get(input, 'reason.id', -1) === 5">
            <v-textarea
              outlined
              rows="4"
              no-resize
              v-model="input.otherReasonDescription"
              label="Beskriv annan orsak"
              :rules="[rules.required]"
            ></v-textarea>
          </template>

          <!-- Enter misprint details content -->
          <template v-if="get(input, 'reason.id', -1) === 4">
            <v-text-field
              label="Detaljer om tryckfel"
              placeholder="Vilka tryckfel finns på det yrkesbevis du fått från BYN?"
              outlined
              :rules="[rules.required]"
              v-model="input.misprintDetails"
            ></v-text-field>
          </template>

          <!-- Enter new name content -->
          <template v-if="get(input, 'reason.id', -1) === 3">
            <v-text-field
              label="Fullständigt namn"
              :placeholder="placeholder"
              outlined
              :rules="[rules.required]"
              v-model="input.recipientOldName"
              v-if="step === 1"
            ></v-text-field>

            <v-text-field
              v-else
              label="Fullständigt namn"
              :placeholder="placeholder"
              outlined
              :rules="[rules.required]"
              v-model="input.recipientNewName"
            ></v-text-field>

            <v-card-actions class="d-flex mt-0 mb-6 justify-space-between" :style="{padding: '0 !important'}">
              <v-btn class="icon" @click="step = 1" :disabled="step === 1">
                <v-icon>$sl-arrow-left</v-icon>
                Bakåt
              </v-btn>

              <v-btn class="icon" @click="step = 2" v-if="step < 2" :disabled="! input.recipientOldName">
                Fortsätt
                <v-icon>$sl-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </template>

          <template v-if="get(input, 'reason.id', null) !== 3">
            <h6 class="mt-6 mb-4">Ange belopp</h6>
            <v-select
              v-model="input.cost"
              :items="costList"
              item-text="cost"
              outlined
              item-value="id"
              label="Välj belopp"
              data-cy="selectedCost"
              :disabled="costFieldDisabled || !input.reason"
              persistent-hint
              return-object
            ></v-select>
          </template>

          <!-- Change email content -->
          <template v-if="hasCost">
            <h6 class="mt-6 mb-6">Betalning</h6>
            <p>Betalningen sker mot faktura som skickas till din hemadress som den är angiven på Mina sidor. Du kan välja att få e-postfaktura istället.</p>
            <v-checkbox
              class="checkbox"
              v-model="changePaymentMethod"
              :label="`Skicka fakturan med e-post (fakturan skickas till ${personEmail})`"
              :value="!changePaymentMethod"
            >
            </v-checkbox>
          </template>
        </template>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn color="secondary" @click="close()">
          <v-icon>$sl-cancel</v-icon>Avbryt
        </v-btn>
        <v-btn 
          @click="orderReplacementCard()"
          :disabled="!isFormValid"
        >
          <v-icon>$sl-check</v-icon>Beställ
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import sortBy from 'lodash/sortBy'
import get from 'lodash/get'
import API from '@/services/api'
import { RulesMixin } from '@/components/mixins'
import MessageBox from '@/components/common/MessageBox.vue'
import pick from 'lodash/pick'

  export default {
    name: 'PersonReplacementCard',

    mixins: [ RulesMixin ],

    components: { MessageBox },

    props: {
      mode: {
        type: String,
        default: ''
      },
      isVisible: {
        type: Boolean,
        default: false
      }
    },

    data: () => ({
      valid: true,
      initialInputValue: {
        reason: null,
        deliveryAddress: {},
        recipientOldName: null,
        recipientNewName: null,
        misprintDetails: null,
        otherReasonDescription: null,
        cost: {
          id: null,
          text: null
        }
      },
      input: {},
      changeAddress: false,
      changeEmail: false,
      changePaymentMethod: false,
      step: 1,
      costFieldDisabled: false
    }),

    created() {
      this.input = { ...this.initialInputValue }
    },

    watch: {
      changePaymentMethod: {
        handler(val) {
          if(! val) {
            this.changeEmail = false
            this.input.email = ''
            this.input.cost = {
              id: null,
              text: null
            }
          }
        }
      },
      
      input: {
        handler(val) {
          if(! val.reason) {
            this.input.recipientOldName = ''
            this.input.recipientNewName = ''
            this.input.misprintDetails = ''
            this.step = 1
          }
        },
        deep: true
      },
      'input.reason': {
        handler(val) {
          // Initially reset states
          this.input.cost = { id: null, text: '' }
          this.costFieldDisabled = false
          
          if ( [2].includes(val?.id) ) {
            this.input.cost = this.costList.find(c => c.id === 2)
            this.costFieldDisabled = true
          }
        }
      },
      isVisible: {
        handler(value) {
          if ( !value )
            this.resetModal()
        }
      }
    },

    computed: {
      ...mapGetters('person', ['selectedPerson']),

      costList() {
        return [
          {
            id: 1, cost: 0 + ' kr', 
          },
          {
            id: 2, cost: 450 + ' kr'
          }
        ]
      },

      reasons() {
        return [
          { id: 1, text: 'Yrkesbevis har ändrats' },
          { id: 2, text: 'Har tappat eller förlorat kortet' },
          { id: 3, text: 'Har bytt namn hos skatteverket' },
          { id: 4, text: 'Det finns tryckfel på kortet' },
          { id: 5, text: 'Annan anledning' }
        ]
      },

      placeholder() {
        if(this.step === 1) {
          return 'Ange ditt gamla namn som det är registrerad hos Skatteverket'
        } else {
          return 'Vad är ditt nya namn som det är registrerad hos Skatteverket?'
        }
      },

      hasCost() {
        return this.input.cost?.id === 2
      },

      selectedOption() {
        return Number(get(this, 'input.reason.id', null))
      },

      isFormValid() {
        let additionalCondition = true

        if ( this.selectedOption === 3 ) {
          //- set no cost
          this.input.cost.id = 1

          const fields = pick(get(this, 'input', null), ['recipientOldName', 'recipientNewName'])
          additionalCondition = !!Object.values(fields).every(o => o)
        }

        if ( this.selectedOption === 4 ) {
          additionalCondition = !!get(this, 'input.misprintDetails', null)
        }

        if ( this.selectedOption === 5 ) {
          additionalCondition = !!get(this, 'input.otherReasonDescription', null)
        }

        return Object.values(this.input.cost).some(c => c) && additionalCondition
      },

      personEmail() {
        let email = this.selectedPerson?.email
        return email
      },
    },

    methods: {
      get,

      displayOrderCost() {
        return this.input.cost?.id > 0 ? ( this.hasCost ? 'Denna beställning medför en kostnad på 450 kr (inklusive moms)' : 'Denna beställning är gratis' ) : ''
      },

      resetModal() {
        Object.assign(this.input, { ...this.initialInputValue })
        this.changePaymentMethod = false
        this.changeEmail = false
      },

      close() {
        this.$emit('close')
        this.resetModal()
      },

      async orderReplacementCard() {

        if(!this.$refs.form.validate()) {
          this.$events.fire('showError', 'Formuläret är inte korrekt ifyllt.')
          return
        }

        const person = this.selectedPerson
        let employmentType = 'apprenticeships'

        if(person?.employments.length) {
          employmentType = 'employments'
        }

        const res = await this.$dialog.confirm({
          title: 'Beställ ersättningskort',
          text: `Är du säker på att du vill beställa ett ersättningskort för ${person.firstName} ${person.lastName} ?`,
          actions: [
            {text: 'Nej', key: false, outlined: true, icon: '$sl-cancel'},
            {
              text: 'JA',
              key: true,
              icon: '$sl-like',
              outlined: false
            }
          ]
        })
        
        if (res !== true) return
        
        const employment = sortBy(person[employmentType], [ function (o) { return !o.manuallyEndedDate && o.fromDate } ])[0] ?? null

        let data = {
          companyId: employment ? employment.company?.id : null,
          companyName: employment ? employment.company?.name : null,
          deliveryAttention: `${person?.firstName} ${person?.lastName}`,
          deliveryCity: person?.city,
          deliveryCountry: person?.country,
          deliveryPostalAddress: person?.address1,
          deliveryPostalAddress2: person?.address2,
          deliveryWorkplaceId: null,
          deliveryZipCode: person?.zip,
          invoiceEmail: this.changePaymentMethod ? this.personEmail : '',
          price: this.hasCost ? 450 : 0,
          reason: `${ 
            [3, 4, 5].includes(this.input.reason.id) ? 
            ` ${ this.input?.reason?.text } - ${ (this.input?.recipientNewName || this.input?.otherReasonDescription || this.input?.misprintDetails) } `: 
            this.input?.reason?.text }` 
        }
        
        if( this.changeAddress ) {
          data = Object.assign(data, { ...this.input?.deliveryAddress })
        }

        API.orderReplacementCard(this.selectedPerson?.id, { data: data }).then(() => {
          this.$events.fire('showInfo', 'Ersättningkort beställd.')
        })
        .catch((error) => {
          console.log('There was an error: ', error);
          return this.$events.fire('showError', 'En beställning gick inte att skapa.')
        })
        .finally(() => {
          this.close()
        })
      },

      updateEmail() {
        if(! this.$refs.form.validate()) {
          return this.$events.fire('showError', 'Formuläret är inte korrekt ifyllt.')
        }
        
        this.changeEmail = false
        return this.$events.fire('showInfo', 'E-postadress ändrad.')
      }
    }
  }
</script>

<style lang="scss">
  .v-input--checkbox .v-label {
    font-size: 14px;
  }
</style>